import React, { useCallback, useEffect, useState } from 'react';
import PaymentRequestsTab from '../components/finance/PaymentRequestsTab';
import LiquidationsTab from '../components/finance/LiquidationsTab';
import ReplenishmentsTab from '../components/finance/ReplenishmentsTab';
import { financeApi, resolveFinanceApiError } from '../services/financeApi';
import { branchApi, resolveBranchApiError } from '../services/branchApi';
import useScopePermissions from '../hooks/useScopePermissions';
import { useAuth } from '../context/AuthContext';
import './FinancePage.css';

// QA 2026-07: the Replenishment tab is temporarily hidden (NOT removed) —
// Petty Cash requests are liquidated through the Liquidation tab for now.
// Flip this back to true to restore the tab; all replenishment code stays intact.
const SHOW_REPLENISHMENTS_TAB = false;

const TABS = [
  { key: 'payment-requests', label: 'Request for Payment' },
  { key: 'liquidations', label: 'Liquidation' },
  { key: 'replenishments', label: 'Replenishment' },
];

export default function FinancePage() {
  const { hasScope } = useScopePermissions();
  const { isAdmin } = useAuth();
  const canViewPaymentRequests = hasScope('finance.payment-requests.view');
  const canCreatePaymentRequests = hasScope('finance.payment-requests.create');
  const canUpdatePaymentRequests = hasScope('finance.payment-requests.update');
  const canViewLiquidations = hasScope('finance.liquidations.view');
  const canCreateLiquidations = hasScope('finance.liquidations.create');
  const canUpdateLiquidations = hasScope('finance.liquidations.update');
  const canUploadLiquidations = hasScope('finance.liquidations.upload');
  const canViewReplenishments = hasScope('finance.replenishments.view');
  const canCreateReplenishments = hasScope('finance.replenishments.create');
  const canUpdateReplenishments = hasScope('finance.replenishments.update');

  const [activeTab, setActiveTab] = useState('payment-requests');
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [liquidations, setLiquidations] = useState([]);
  const [replenishments, setReplenishments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const loadData = useCallback(async (options = {}) => {
    const { clearFeedback = true } = options;

    setIsLoading(true);
    setError('');
    if (clearFeedback) {
      setMessage('');
    }

    try {
      // Load each dataset independently so a permission gap on one tab (e.g. a user
      // who can view payment requests but not liquidations) does not blank the
      // datasets the user IS allowed to see.
      const [
        paymentRequestsResult,
        liquidationsResult,
        replenishmentsResult,
        branchesResult,
      ] = await Promise.allSettled([
        financeApi.listPaymentRequests({ bustCache: true }),
        financeApi.listLiquidations({ bustCache: true }),
        financeApi.listReplenishments({ bustCache: true }),
        branchApi.listBranches({ bustCache: true }),
      ]);

      setPaymentRequests(
        paymentRequestsResult.status === 'fulfilled'
          ? (paymentRequestsResult.value?.data?.payment_requests || [])
          : []
      );
      setLiquidations(
        liquidationsResult.status === 'fulfilled'
          ? (liquidationsResult.value?.data?.liquidations || [])
          : []
      );
      setReplenishments(
        replenishmentsResult.status === 'fulfilled'
          ? (replenishmentsResult.value?.data?.replenishments || [])
          : []
      );
      setBranches(
        branchesResult.status === 'fulfilled'
          ? (branchesResult.value?.data?.branches || [])
          : []
      );

      // Only surface an error if a dataset the user can actually view failed to load.
      const failed = [];
      if (canViewPaymentRequests && paymentRequestsResult.status === 'rejected') {
        failed.push(resolveFinanceApiError(paymentRequestsResult.reason));
      }
      if (canViewLiquidations && liquidationsResult.status === 'rejected') {
        failed.push(resolveFinanceApiError(liquidationsResult.reason));
      }
      if (canViewReplenishments && replenishmentsResult.status === 'rejected') {
        failed.push(resolveFinanceApiError(replenishmentsResult.reason));
      }
      if (failed.length > 0) {
        setError(failed.find(Boolean) || 'Failed to load finance module data.');
      }
    } catch (err) {
      setPaymentRequests([]);
      setLiquidations([]);
      setReplenishments([]);
      setBranches([]);
      const messageText = resolveFinanceApiError(err)
        || resolveBranchApiError(err)
        || 'Failed to load finance module data.';
      setError(messageText);
    } finally {
      setIsLoading(false);
    }
  }, [canViewPaymentRequests, canViewLiquidations, canViewReplenishments]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const visibleTabs = [];
    if (canViewPaymentRequests) {
      visibleTabs.push('payment-requests');
    }
    if (canViewLiquidations) {
      visibleTabs.push('liquidations');
    }
    if (SHOW_REPLENISHMENTS_TAB && canViewReplenishments) {
      visibleTabs.push('replenishments');
    }

    if (visibleTabs.length === 0) {
      return;
    }

    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [activeTab, canViewLiquidations, canViewPaymentRequests, canViewReplenishments]);

  async function runSubmission(action, successMessage) {
    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      await action();
      setMessage(successMessage);
      await loadData({ clearFeedback: false });
      return true;
    } catch (err) {
      setError(resolveFinanceApiError(err));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCreatePaymentRequest(payload) {
    if (!canCreatePaymentRequests) {
      setError('You do not have permission to create payment requests.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.createPaymentRequest(payload),
      'Payment request created successfully.'
    );
  }

  function handleUpdatePaymentRequest(paymentRequestId, payload) {
    if (!canUpdatePaymentRequests && !isAdmin) {
      setError('You do not have permission to update payment requests.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.updatePaymentRequest(paymentRequestId, payload),
      'Payment request updated successfully.'
    );
  }

  function handleCreateLiquidation(payload) {
    if (!canCreateLiquidations) {
      setError('You do not have permission to create liquidations.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.createLiquidation(payload),
      'Liquidation created successfully.'
    );
  }

  function handleUpdateLiquidation(liquidationId, payload) {
    if (!canUpdateLiquidations && !isAdmin) {
      setError('You do not have permission to update liquidations.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.updateLiquidation(liquidationId, payload),
      'Liquidation updated successfully.'
    );
  }

  async function handleUploadLiquidationAttachment(file) {
    if (!canUploadLiquidations && !canCreateLiquidations) {
      throw new Error('You do not have permission to upload liquidation attachments.');
    }

    try {
      return await financeApi.uploadLiquidationAttachment(file);
    } catch (err) {
      throw new Error(resolveFinanceApiError(err));
    }
  }

  function handleUpdateReplenishment(replenishmentId, payload) {
    if (!canUpdateReplenishments && !isAdmin) {
      setError('You do not have permission to update replenishments.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.updateReplenishment(replenishmentId, payload),
      'Replenishment updated successfully.'
    );
  }

  function handleCreateReplenishment(payload) {
    if (!canCreateReplenishments) {
      setError('You do not have permission to create replenishments.');
      return Promise.resolve(false);
    }

    return runSubmission(
      () => financeApi.createReplenishment(payload),
      'Replenishment created successfully.'
    );
  }

  return (
    <div className="dashboard-container">
      <section className="dashboard-header">
        <h1>Finance</h1>
        <p>
          {SHOW_REPLENISHMENTS_TAB
            ? 'Manage payment requests, liquidations, and replenishments.'
            : 'Manage payment requests and liquidations.'}
        </p>
      </section>

      <section className="dashboard-content finance-page-content">
        <div className="finance-tabs">
          {TABS.filter((tab) => {
            if (tab.key === 'payment-requests') {
              return canViewPaymentRequests;
            }
            if (tab.key === 'liquidations') {
              return canViewLiquidations;
            }
            if (tab.key === 'replenishments') {
              return SHOW_REPLENISHMENTS_TAB && canViewReplenishments;
            }
            return false;
          }).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`finance-tab-btn ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {error ? <div className="error-box">{error}</div> : null}
        {message ? <div className="success-box">{message}</div> : null}

        {activeTab === 'payment-requests' && canViewPaymentRequests ? (
          <PaymentRequestsTab
            paymentRequests={paymentRequests}
            branches={branches}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            onCreateRequest={handleCreatePaymentRequest}
            onUpdateRequest={handleUpdatePaymentRequest}
            canCreateRequest={canCreatePaymentRequests}
            canUpdateRequest={canUpdatePaymentRequests}
          />
        ) : null}

        {activeTab === 'liquidations' && canViewLiquidations ? (
          <LiquidationsTab
            liquidations={liquidations}
            paymentRequests={paymentRequests}
            branches={branches}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            onCreateLiquidation={handleCreateLiquidation}
            onUpdateLiquidation={handleUpdateLiquidation}
            onUploadLiquidationAttachment={handleUploadLiquidationAttachment}
            canCreateLiquidation={canCreateLiquidations}
            canUpdateLiquidation={canUpdateLiquidations}
            canUploadAttachment={canUploadLiquidations || canCreateLiquidations}
          />
        ) : null}

        {SHOW_REPLENISHMENTS_TAB && activeTab === 'replenishments' && canViewReplenishments ? (
          <ReplenishmentsTab
            replenishments={replenishments}
            paymentRequests={paymentRequests}
            branches={branches}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            onCreateReplenishment={handleCreateReplenishment}
            onUpdateReplenishment={handleUpdateReplenishment}
            canCreateReplenishment={canCreateReplenishments}
            canUpdateReplenishment={canUpdateReplenishments}
          />
        ) : null}

        {!canViewPaymentRequests && !canViewLiquidations && !(SHOW_REPLENISHMENTS_TAB && canViewReplenishments) ? (
          <div className="dashboard-empty">No finance tabs are available for your account.</div>
        ) : null}
      </section>
    </div>
  );
}
